import { forwardRef } from 'react';
import type { IconName } from './icon-names';
import { cn } from '@/utils/cn';

// Icon sizes use Primer icon spacing tokens (--spacing-icon-* from tailwind.css).
// In Tailwind v4, size-{name} resolves to --spacing-{name}:
//   size-icon-small  → --spacing-icon-small  → 0.75rem (12px)
//   size-icon-medium → --spacing-icon-medium → 1rem    (16px)
//   size-icon-large  → --spacing-icon-large  → 1.5rem  (24px)
//   size-icon-xl     → --spacing-icon-xl     → 2.5rem  (40px)
const sizeClasses = {
  small: 'size-icon-small',
  medium: 'size-icon-medium',
  large: 'size-icon-large',
  xl: 'size-icon-xl',
} as const;

export type IconSize = keyof typeof sizeClasses;

/**
 * Primer icon colour tokens.
 *
 * Each value maps to an `icon-*` utility class defined in
 * `src/primer/utilities.css`, which in turn references a `--color-icon-*`
 * CSS custom property from the Supernova-generated token files. These tokens
 * are distinct from the `text-*` colour tokens — they are tuned specifically
 * for icon legibility at each semantic state (e.g. `interactive` uses the
 * icon-specific purple shade, not the text-interactive shade).
 *
 * Use `color` when the icon carries explicit semantic meaning (e.g. an error
 * indicator, a disabled control). Omit it to inherit colour from the
 * surrounding text via `currentColor` — the right default for most icons.
 */
export type IconColor =
  | 'default'
  | 'disabled'
  | 'error'
  | 'interactive'
  | 'success'
  | 'new'
  | 'white'
  | 'light'
  | 'warning'
  | 'info';

const colorClasses: Record<IconColor, string> = {
  default: 'icon-default',
  disabled: 'icon-disabled',
  error: 'icon-error',
  interactive: 'icon-interactive',
  success: 'icon-success',
  new: 'icon-new',
  white: 'icon-white',
  light: 'icon-light',
  warning: 'icon-warning',
  info: 'icon-info',
};

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  /** Icon to render — must be a filename from src/assets/icons/ (without .svg). */
  name: IconName;
  /**
   * Size of the icon. Defaults to 'medium'.
   * Uses Primer icon spacing tokens: small = 12px, medium = 16px, large = 24px, xl = 40px.
   */
  size?: IconSize;
  /**
   * Primer icon colour token. When set, applies the corresponding `icon-*`
   * utility class which references a `--color-icon-*` token from the design
   * system. Omit to inherit colour from the parent via `currentColor`.
   */
  color?: IconColor;
  /** Additional Tailwind classes. */
  className?: string;
}

/**
 * Primer Icon component.
 *
 * Renders an SVG icon from the sprite sheet generated by vite-plugin-svg-icons.
 * Colour defaults to `currentColor` (inherited from the parent). For icons
 * that carry explicit semantic meaning, use the `color` prop to apply a Primer
 * icon colour token instead.
 *
 * @example
 * // Inherits parent text colour (most common usage)
 * <Icon name="star" />
 *
 * @example
 * // Explicit Primer icon colour
 * <Icon name="star" size="large" color="interactive" />
 * <Icon name="bell" color="disabled" />
 * <Icon name="send" color="error" />
 *
 * @example
 * // className still accepted for one-off overrides (e.g. text-* utilities)
 * <Icon name="star" className="text-interactive" />
 */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, size = 'medium', color, className, ...props },
  ref
) {
  return (
    <svg
      ref={ref}
      aria-hidden="true"
      focusable="false"
      className={cn(sizeClasses[size], 'fill-current', color && colorClasses[color], className)}
      {...props}
    >
      <use href={`#icon-${name}`} />
    </svg>
  );
});
